import { useState, useCallback } from 'react';
import type { FlowPhase } from '../types/flowchart.types';
import { FLOW_TOKENS as T } from '../shared/flowTokens';

export function useFlowPhases() {
  const [phases, setPhases] = useState<FlowPhase[]>([]);

  const handleAddPhase = useCallback((name: string) => {
    setPhases((prev) => {
      const newPhases = [
        ...prev,
        {
          id: `phase_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
          name,
          width: 0,
        },
      ];
      const equalWidth = Math.floor(T.canvas.a3Width / newPhases.length);
      return newPhases.map((p, i) => ({
        ...p,
        width:
          i === newPhases.length - 1
            ? T.canvas.a3Width - equalWidth * (newPhases.length - 1)
            : equalWidth,
      }));
    });
  }, []);

  const handleRemovePhase = useCallback((id: string) => {
    setPhases((prev) => {
      if (prev.length <= 1) return prev;
      const remaining = prev.filter((p) => p.id !== id);
      const equalWidth = Math.floor(T.canvas.a3Width / remaining.length);
      return remaining.map((p, i) => ({
        ...p,
        width:
          i === remaining.length - 1
            ? T.canvas.a3Width - equalWidth * (remaining.length - 1)
            : equalWidth,
      }));
    });
  }, []);

  const handleRenamePhase = useCallback((id: string, name: string) => {
    setPhases((prev) => prev.map((p) => (p.id === id ? { ...p, name } : p)));
  }, []);

  const STEP = 20;
  const MIN_W = 60;

  const handleResizePhase = useCallback((id: string, direction: 'left' | 'right') => {
    setPhases((prev) => {
      const idx = prev.findIndex((p) => p.id === id);
      if (idx < 0) return prev;
      const neighborIdx = direction === 'right' ? idx + 1 : idx - 1;
      if (neighborIdx < 0 || neighborIdx >= prev.length) return prev;
      if (prev[neighborIdx].width <= MIN_W) return prev;
      return prev.map((p, i) => {
        if (i === idx) return { ...p, width: p.width + STEP };
        if (i === neighborIdx) return { ...p, width: Math.max(MIN_W, p.width - STEP) };
        return p;
      });
    });
  }, []);

  return {
    phases,
    setPhases,
    handleAddPhase,
    handleRemovePhase,
    handleRenamePhase,
    handleResizePhase,
  };
}
